import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { fetchClubCatalogItems } from "../../api/clubApi";
import EventImageWithFallback from "../eventImageWithFallback/EventImageWithFallback";
import {
  CATALOG_EVENT_STOCK_MAX,
  CATALOG_EVENT_STOCK_MIN,
  CATALOG_KIND_ADDON,
  CATALOG_KIND_PREORDER,
  eventCatalogSnapshots,
  formatCatalogMoney,
  unwrapCatalogList,
} from "../../helpers/clubCatalog";
import "../../pages/clubCatalog/clubCatalog.css";

function CatalogPickList({
  items,
  selectedIds,
  overrides,
  stockQty,
  snapshots,
  onToggle,
  onOverrideChange,
  onStockChange,
  catalogManageHref,
  canManageCatalog,
  t,
}) {
  const snapById = useMemo(
    () => new Map((snapshots || []).map((row) => [row.itemId, row])),
    [snapshots]
  );

  if (!items.length) {
    return (
      <p className="text-muted small mb-0">
        {t("CATALOG_EMPTY_PICKER")}
        {canManageCatalog && catalogManageHref ? (
          <>
            {" "}
            <Link to={catalogManageHref}>{t("CATALOG_MANAGE_LINK")}</Link>
          </>
        ) : null}
      </p>
    );
  }

  return (
    <div className="club-catalog-picker-grid">
      {items.map((item) => {
        const checked = selectedIds.includes(item._id);
        const overrideVal = overrides?.[item._id];
        const stockVal = stockQty?.[item._id] ?? "";
        const snap = snapById.get(item._id);
        const qtySold = Number(snap?.qtySold) || 0;
        const soldOut = Boolean(snap?.soldOut) || snap?.available === false;
        return (
          <div
            key={item._id}
            className={`club-catalog-pick-row ${
              item.isactive ? "" : "is-inactive"
            }${checked ? " is-selected" : ""}`}
          >
            <div className="club-catalog-pick-main">
              <Form.Check
                type="checkbox"
                checked={checked}
                disabled={!item.isactive && !checked}
                onChange={() => onToggle(item._id)}
                title={
                  !item.isactive && !checked
                    ? t("CATALOG_INACTIVE_CANNOT_ATTACH")
                    : undefined
                }
              />
              {item.image ? (
                <EventImageWithFallback
                  src={item.image}
                  alt={item.name}
                  className="club-catalog-thumb"
                />
              ) : (
                <span className="club-catalog-thumb club-catalog-thumb--ph">
                  <i className="bx bx-image" />
                </span>
              )}
              <div className="club-catalog-pick-copy">
                <span className="club-catalog-item-name d-block">{item.name}</span>
                <span className="text-muted small">
                  {formatCatalogMoney(item.unitPrice, item.currency)}
                  {!item.isactive ? ` · ${t("CLUB_STATUS_INACTIVE")}` : ""}
                </span>
                {checked && snap ? (
                  <span className="club-catalog-stock-meta d-block">
                    {snap.unlimited
                      ? t("CATALOG_EVENT_STOCK_UNLIMITED", { sold: qtySold })
                      : t("CATALOG_EVENT_STOCK_SOLD", {
                          sold: qtySold,
                          left: snap.remaining ?? "—",
                        })}
                    {soldOut ? (
                      <Badge bg="danger" className="ms-2">
                        {snap.soldOut
                          ? t("CATALOG_EVENT_SOLD_OUT")
                          : t("CATALOG_EVENT_NOT_AVAILABLE")}
                      </Badge>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
            {checked ? (
              <div className="club-catalog-pick-fields">
                <div className="club-catalog-attach-field">
                  <Form.Label className="club-catalog-stock-label">
                    {t("CATALOG_EVENT_PRICE_LABEL")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    className="club-catalog-override"
                    value={overrideVal ?? ""}
                    placeholder={String(item.unitPrice)}
                    onChange={(e) => onOverrideChange(item._id, e.target.value)}
                    title={t("CATALOG_EVENT_PRICE_OVERRIDE")}
                  />
                </div>
                <div className="club-catalog-attach-field">
                  <Form.Label className="club-catalog-stock-label">
                    {t("CATALOG_EVENT_STOCK_LABEL")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={Math.max(CATALOG_EVENT_STOCK_MIN, qtySold || 0)}
                    max={CATALOG_EVENT_STOCK_MAX}
                    step={1}
                    inputMode="numeric"
                    className="club-catalog-stock-input"
                    value={stockVal}
                    placeholder={t("CATALOG_EVENT_STOCK_UNLIMITED_PH")}
                    onChange={(e) => onStockChange(item._id, e.target.value)}
                    title={t("CATALOG_EVENT_STOCK_HELP")}
                  />
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Multi-select add-ons / preorder from the club catalog.
 * Stock (qty) is event-level, not club catalog stock.
 */
const EventCatalogAttachSection = ({
  control,
  setValue,
  clubId,
  eventItem,
  catalogManageHref,
  canManageCatalog = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [loadError, setLoadError] = useState("");

  const addonItemIds = useWatch({ control, name: "addonItemIds" }) || [];
  const preorderItemIds = useWatch({ control, name: "preorderItemIds" }) || [];
  const addonPriceOverrides =
    useWatch({ control, name: "addonPriceOverrides" }) || {};
  const preorderPriceOverrides =
    useWatch({ control, name: "preorderPriceOverrides" }) || {};
  const addonStockQty = useWatch({ control, name: "addonStockQty" }) || {};
  const preorderStockQty = useWatch({ control, name: "preorderStockQty" }) || {};
  const prevClubRef = useRef(clubId);

  useEffect(() => {
    if (prevClubRef.current && clubId && prevClubRef.current !== clubId) {
      setValue("addonItemIds", [], { shouldDirty: true });
      setValue("preorderItemIds", [], { shouldDirty: true });
      setValue("addonPriceOverrides", {}, { shouldDirty: true });
      setValue("preorderPriceOverrides", {}, { shouldDirty: true });
      setValue("addonStockQty", {}, { shouldDirty: true });
      setValue("preorderStockQty", {}, { shouldDirty: true });
    }
    prevClubRef.current = clubId;
  }, [clubId, setValue]);

  const snapshots = useMemo(
    () => eventCatalogSnapshots(eventItem),
    [eventItem]
  );

  useEffect(() => {
    if (!clubId) {
      setAddons([]);
      setPreorders([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    Promise.all([
      fetchClubCatalogItems(clubId, { kind: CATALOG_KIND_ADDON }),
      fetchClubCatalogItems(clubId, { kind: CATALOG_KIND_PREORDER }),
    ])
      .then(([addonRes, preorderRes]) => {
        if (cancelled) return;
        setAddons(
          unwrapCatalogList(addonRes).addons.filter((i) => i.isactive)
        );
        setPreorders(
          unwrapCatalogList(preorderRes).preorders.filter((i) => i.isactive)
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setAddons([]);
        setPreorders([]);
        setLoadError(
          typeof err === "string" ? err : err?.message || t("CATALOG_LOAD_ERROR")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, t]);

  const mergeWithSnapshot = (activeItems, snapshotRows, selectedIds) => {
    const byId = new Map(activeItems.map((i) => [i._id, i]));
    for (const snap of snapshotRows) {
      if (!byId.has(snap.itemId) && selectedIds.includes(snap.itemId)) {
        byId.set(snap.itemId, {
          _id: snap.itemId,
          name: snap.name,
          description: snap.description,
          image: snap.image,
          unitPrice: snap.unitPrice,
          currency: snap.currency,
          isactive: false,
          kind: "ADDON",
          sortOrder: 0,
        });
      }
    }
    return Array.from(byId.values());
  };

  const addonChoices = useMemo(
    () => mergeWithSnapshot(addons, snapshots.addons, addonItemIds),
    [addons, snapshots.addons, addonItemIds]
  );
  const preorderChoices = useMemo(
    () => mergeWithSnapshot(preorders, snapshots.preorders, preorderItemIds),
    [preorders, snapshots.preorders, preorderItemIds]
  );

  const toggleId = (field, id) => {
    const current = field === "addonItemIds" ? addonItemIds : preorderItemIds;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    setValue(field, next, { shouldDirty: true });
  };

  const setMapField = (field, current, id, value) => {
    setValue(field, { ...current, [id]: value }, { shouldDirty: true });
  };

  if (!clubId) return null;

  return (
    <div className="club-catalog-picker etb-root">
      <div className="titleBtnRowStlye event-table-booking-header-row mb-2">
        <div className="addTicketTitleStyle">{t("CATALOG_EVENT_SECTION")}</div>
      </div>
      {loading ? (
        <p className="text-muted">
          <Spinner size="sm" animation="border" className="me-2" />
          {t("LOADING")}
        </p>
      ) : null}
      {loadError ? <p className="text-danger small">{loadError}</p> : null}

      <h3 className="h6 mb-1">{t("CATALOG_EVENT_ADDONS")}</h3>
      <p className="text-muted small">{t("CATALOG_ADDON_HELP")}</p>
      <CatalogPickList
        items={addonChoices}
        selectedIds={addonItemIds}
        overrides={addonPriceOverrides}
        stockQty={addonStockQty}
        snapshots={snapshots.addons}
        onToggle={(id) => toggleId("addonItemIds", id)}
        onOverrideChange={(id, v) =>
          setMapField("addonPriceOverrides", addonPriceOverrides, id, v)
        }
        onStockChange={(id, v) =>
          setMapField("addonStockQty", addonStockQty, id, v)
        }
        catalogManageHref={catalogManageHref}
        canManageCatalog={canManageCatalog}
        t={t}
      />

      <h3 className="h6 mt-4 mb-1">{t("CATALOG_EVENT_PREORDERS")}</h3>
      <p className="text-muted small">{t("CATALOG_PREORDER_HELP")}</p>
      <CatalogPickList
        items={preorderChoices}
        selectedIds={preorderItemIds}
        overrides={preorderPriceOverrides}
        stockQty={preorderStockQty}
        snapshots={snapshots.preorders}
        onToggle={(id) => toggleId("preorderItemIds", id)}
        onOverrideChange={(id, v) =>
          setMapField(
            "preorderPriceOverrides",
            preorderPriceOverrides,
            id,
            v
          )
        }
        onStockChange={(id, v) =>
          setMapField("preorderStockQty", preorderStockQty, id, v)
        }
        catalogManageHref={catalogManageHref}
        canManageCatalog={canManageCatalog}
        t={t}
      />
    </div>
  );
};

export default EventCatalogAttachSection;
